<script lang="ts" setup>
  import { parseDescription } from './description';
  import type { VFeatureBadgeCardProps } from './types';

  const props = defineProps<VFeatureBadgeCardProps>();

  // a card touching the target takes the pointer on its edge, and the two flicker
  const GAP = 8;
  const CLOSE_DELAY = 300;

  const open = ref(false);
  const card = ref<HTMLElement>();
  const at = ref({ top: 0, left: 0 });
  let closing: ReturnType<typeof setTimeout> | undefined;

  const tokens = computed(() => parseDescription(props.text));
  const plainText = computed(() =>
    tokens.value.map((token) => token.text).join(''),
  );

  const place = () => {
    if (!props.target) {
      return;
    }

    const rect = props.target.getBoundingClientRect();
    const width = card.value?.offsetWidth ?? 320;
    const height = card.value?.offsetHeight ?? 0;

    const below = rect.bottom + GAP;
    const above = rect.top - GAP - height;
    const flip = below + height > window.innerHeight && above >= 8;

    at.value = {
      top: flip ? above : below,
      left: Math.min(Math.max(8, rect.left), window.innerWidth - width - 8),
    };
  };

  const show = () => {
    clearTimeout(closing);
    open.value = true;
    nextTick(place);
  };

  const hide = () => {
    clearTimeout(closing);
    closing = setTimeout(() => {
      open.value = false;
    }, CLOSE_DELAY);
  };

  const close = () => {
    clearTimeout(closing);
    open.value = false;
  };

  const listening = computed(() => (open.value ? window : null));

  useEventListener(listening, 'scroll', place, true);
  useEventListener(listening, 'resize', place);
  useEventListener(listening, 'keydown', (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      close();
    }
  });

  useEventListener(listening, 'pointerdown', (event: PointerEvent) => {
    const node = event.target as Node;

    if (!card.value?.contains(node) && !props.target?.contains(node)) {
      close();
    }
  });

  onBeforeUnmount(() => clearTimeout(closing));

  defineExpose({ show, hide });
</script>

<template>
  <Teleport to="body">
    <span v-if="plainText" :id="textId" class="sr-only">{{ plainText }}</span>
    <div
      v-if="open"
      ref="card"
      class="fixed z-[1100] max-w-[320px] p-[10px] rounded-md bg-[#cbcdd2] text-sm text-black leading-tight whitespace-pre-line break-words"
      :style="{ top: `${at.top}px`, left: `${at.left}px` }"
      data-testid="featureBadgeCard"
      @mouseenter="show"
      @mouseleave="hide">
      <template v-for="(token, index) in tokens" :key="index">
        <RouterLink
          v-if="token.href?.startsWith('/')"
          class="text-blue underline"
          :to="token.href"
          @click="close"
          >{{ token.text }}</RouterLink
        >
        <a
          v-else-if="token.href"
          class="text-blue underline"
          :href="token.href"
          rel="noopener noreferrer"
          target="_blank"
          >{{ token.text }}</a
        >
        <template v-else>{{ token.text }}</template>
      </template>
    </div>
  </Teleport>
</template>
